import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import authService from '../services/authService';
import cognitoAuthService from '../services/cognitoAuthService';
import config from '../config';

// Select auth service based on configuration
const getAuthService = () => {
  if (config.auth?.useCognito) {
    console.log('Using Cognito authentication');
    return cognitoAuthService;
  }
  console.log('Using legacy authentication');
  return authService;
};

const activeAuthService = getAuthService();

// Create the Auth Context
export const AuthContext = createContext(null);

// Token refresh configuration
const TOKEN_REFRESH_THRESHOLD_MINUTES = 30; // Refresh when token has less than 30 minutes left
const ACTIVITY_CHECK_INTERVAL_MS = 120000; // Check every 2 minutes
const INACTIVITY_TIMEOUT_MS = 20 * 60 * 1000; // Consider user inactive after 20 minutes of no activity
const ACTIVITY_REFRESH_DEBOUNCE_MS = 30000; // Debounce activity-triggered refresh to at most once per 30s

/**
 * Decode JWT token and extract expiration time.
 * @param {string} token - JWT token string
 * @returns {number|null} Expiration timestamp in seconds, or null if invalid
 */
const getTokenExpiration = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp || null;
  } catch (error) {
    console.warn('Failed to decode token:', error);
    return null;
  }
};

/**
 * Check if token is nearing expiration.
 * @param {string} token - JWT token string
 * @param {number} thresholdMinutes - Threshold in minutes before expiration
 * @returns {boolean} True if token will expire within threshold
 */
const isTokenNearExpiration = (token, thresholdMinutes = TOKEN_REFRESH_THRESHOLD_MINUTES) => {
  const expiration = getTokenExpiration(token);
  if (!expiration) return true; // If we can't decode, assume it needs refresh
  
  const now = Math.floor(Date.now() / 1000);
  const timeUntilExpiry = expiration - now;
  const thresholdSeconds = thresholdMinutes * 60;
  
  return timeUntilExpiry <= thresholdSeconds;
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastActivityRef = useRef(Date.now());
  const refreshIntervalRef = useRef(null);
  const isRefreshingRef = useRef(false);
  const checkAndRefreshTokenRef = useRef(null);
  const activityRefreshDebounceRef = useRef(null);
  
  /**
   * Update the last activity timestamp when user interacts with the app.
   * If the token is within 30 minutes of expiry, also triggers a debounced refresh.
   */
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();

    // Debounced activity-triggered refresh: if token is near expiry, refresh immediately on interaction
    if (activityRefreshDebounceRef.current) return;

    const token = localStorage.getItem('token');
    if (token && isTokenNearExpiration(token)) {
      activityRefreshDebounceRef.current = setTimeout(() => {
        activityRefreshDebounceRef.current = null;
      }, ACTIVITY_REFRESH_DEBOUNCE_MS);
      checkAndRefreshTokenRef.current?.();
    }
  }, []);
  
  /**
   * Check if user has been active recently.
   * @returns {boolean} True if user was active within the inactivity timeout
   */
  const isUserActive = useCallback(() => {
    return (Date.now() - lastActivityRef.current) < INACTIVITY_TIMEOUT_MS;
  }, []);
  
  /**
   * Perform proactive token refresh if needed.
   * Only refreshes if user is active and token is nearing expiration.
   */
  const checkAndRefreshToken = useCallback(async () => {
    // Prevent concurrent refresh attempts
    if (isRefreshingRef.current) return;
    
    const token = localStorage.getItem('token');
    if (!token) return;
    
    // Only refresh if user is actively using the application
    if (!isUserActive()) {
      console.log('User inactive, skipping proactive token refresh');
      return;
    }
    
    // Check if token is nearing expiration
    if (!isTokenNearExpiration(token)) {
      return; // Token still has plenty of time
    }
    
    console.log('Token nearing expiration, refreshing...');
    isRefreshingRef.current = true;
    
    try {
      const response = await activeAuthService.refreshToken(token);
      if (response.access_token) {
        localStorage.setItem('token', response.access_token);
        console.log('Token refreshed successfully');
      }
    } catch (error) {
      console.warn('Proactive token refresh failed:', error);
      // Don't logout here - let the user continue until their request fails
    } finally {
      isRefreshingRef.current = false;
    }
  }, [isUserActive]);

  // Keep ref in sync so updateActivity (with empty deps) can always call the latest version
  checkAndRefreshTokenRef.current = checkAndRefreshToken;
  
  // Set up activity listeners and token refresh interval
  useEffect(() => {
    // Activity events to track
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    // Add activity listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });
    
    // Set up periodic token refresh check
    refreshIntervalRef.current = setInterval(checkAndRefreshToken, ACTIVITY_CHECK_INTERVAL_MS);
    
    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      if (activityRefreshDebounceRef.current) {
        clearTimeout(activityRefreshDebounceRef.current);
      }
    };
  }, [updateActivity, checkAndRefreshToken]);
  
  // Check if user is already logged in (from localStorage)
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      const userInfo = localStorage.getItem('user');
      
      if (token && userInfo) {
        try {
          // Check if token needs immediate refresh
          if (isTokenNearExpiration(token)) {
            console.log('Token near expiration on load, refreshing...');
            try {
              const response = await activeAuthService.refreshToken(token);
              if (response.access_token) {
                localStorage.setItem('token', response.access_token);
              }
            } catch (refreshError) {
              console.warn('Token refresh on load failed:', refreshError);
            }
          }
          
          // Verify token is still valid by getting current user (automatic call)
          const currentToken = localStorage.getItem('token');
          const currentUser = await activeAuthService.getCurrentUser(currentToken, false);
          setUser(currentUser);
        } catch (error) {
          // Token is invalid, clear localStorage
          console.warn('Token validation failed:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        }
      }
      setLoading(false);
    };
    
    checkAuth();
  }, []);
  
  const login = async (username, password) => {
    try {
      // Login with auth service
      const tokenData = await activeAuthService.login(username, password);
      
      // Check if MFA is required
      if (tokenData.mfa_required) {
        return { 
          success: false, 
          mfa_required: true,
          session_token: tokenData.session || tokenData.session_token,
          username: username,
          mfa_method: tokenData.mfa_method || 'totp',
          message: tokenData.message
        };
      }
      
      // Check if MFA setup is required (user needs to configure authenticator)
      if (tokenData.mfa_setup_required) {
        return {
          success: false,
          mfa_setup_required: true,
          session_token: tokenData.session || tokenData.session_token,
          username: username,
          message: tokenData.message
        };
      }
      
      // Check if new password is required (first login with temp password)
      if (tokenData.new_password_required) {
        return {
          success: false,
          new_password_required: true,
          session_token: tokenData.session || tokenData.session_token,
          username: username,
          message: tokenData.message
        };
      }
      
      // Get user information (automatic call during login)
      const userData = await activeAuthService.getCurrentUser(tokenData.access_token, false);
      
      // Store in localStorage
      localStorage.setItem('token', tokenData.access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Update state
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };
  
  const verifyMfa = async (sessionToken, code, username = null) => {
    try {
      // Verify MFA code (Cognito needs username)
      const tokenData = await activeAuthService.verifyMfa(sessionToken, code, username);
      
      // Get user information
      const userData = await activeAuthService.getCurrentUser(tokenData.access_token, false);
      
      // Store in localStorage
      localStorage.setItem('token', tokenData.access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Update state
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };
  
  const setNewPassword = async (username, session, newPassword) => {
    try {
      // Call setNewPassword on auth service (Cognito only)
      if (!activeAuthService.setNewPassword) {
        throw new Error('setNewPassword not available with current auth service');
      }
      
      const result = await activeAuthService.setNewPassword(username, session, newPassword);
      
      // Check if MFA is now required after password change
      if (result.mfa_required) {
        return {
          success: false,
          mfa_required: true,
          session_token: result.session,
          username: username,
          message: result.message
        };
      }
      
      // Check if MFA setup is required after password change
      if (result.mfa_setup_required) {
        return {
          success: false,
          mfa_setup_required: true,
          session_token: result.session,
          username: username,
          message: result.message
        };
      }
      
      // Password changed and logged in successfully
      if (result.access_token) {
        // Get user information
        const userData = await activeAuthService.getCurrentUser(result.access_token, false);
        
        // Store in localStorage
        localStorage.setItem('token', result.access_token);
        localStorage.setItem('user', JSON.stringify(userData));
        
        // Update state
        setUser(userData);
        
        return { success: true };
      }
      
      return { success: true };
    } catch (error) {
      throw error; // Re-throw to let the component handle it
    }
  };
  
  const logout = async () => {
    const token = localStorage.getItem('token');
    
    if (token) {
      try {
        // Notify auth service of logout
        await activeAuthService.logout(token);
      } catch (error) {
        console.warn('Logout request failed:', error);
      }
    }
    
    // Clear local storage and state
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };
  
  const refreshToken = async () => {
    const token = localStorage.getItem('token');
    
    if (token) {
      try {
        const response = await activeAuthService.refreshToken(token);
        // Update with the new token
        if (response.access_token) {
          localStorage.setItem('token', response.access_token);
        }
        return true;
      } catch (error) {
        console.warn('Token refresh failed:', error);
        logout();
        return false;
      }
    }
    return false;
  };
  
  const hasPermission = (permission) => {
    if (!user) return false;
    // Check standard permissions array
    if (user.permissions && (user.permissions.includes(permission) || user.permissions.includes('admin'))) {
      return true;
    }
    // Check Cognito groups (admin group grants all permissions, account_admin group grants account_admin)
    if (user.cognito_groups) {
      if (user.cognito_groups.includes('admin')) return true;
      if (user.cognito_groups.includes(permission)) return true;
    }
    return false;
  };
  
  const hasAnyPermission = (permissions) => {
    if (!user) return false;
    return permissions.some(permission => hasPermission(permission));
  };
  
  // Auto-refresh token on API calls (can be called by other components)
  const withTokenRefresh = async (apiCall) => {
    try {
      return await apiCall();
    } catch (error) {
      if (error.message.includes('401') || error.message.includes('unauthorized')) {
        // Try to refresh token
        const refreshed = await refreshToken();
        if (refreshed) {
          // Retry the API call
          return await apiCall();
        }
      }
      throw error;
    }
  };
  
  // Complete login after MFA setup (tokens already stored by cognitoAuthService)
  const completeLoginWithTokens = async (accessToken) => {
    try {
      // Get user information using the access token
      const userData = await activeAuthService.getCurrentUser(accessToken, false);
      
      // Store in localStorage
      localStorage.setItem('token', accessToken);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Update state
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      console.warn('Failed to complete login:', error);
      return { success: false, error: error.message };
    }
  };

  // Refresh user data from the server (re-fetches /auth/me)
  const refreshUser = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const userData = await activeAuthService.getCurrentUser(token, false);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (error) {
      console.warn('Failed to refresh user data:', error);
      return null;
    }
  };
  
  // Provide the context values to all children
  return (
    <AuthContext.Provider value={{ 
      user, 
      loading,
      login, 
      logout, 
      verifyMfa,
      setNewPassword,
      completeLoginWithTokens,
      refreshUser,
      refreshToken,
      hasPermission,
      hasAnyPermission,
      withTokenRefresh
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
